import React, { useEffect, useRef } from "react";
import styles from "./VenuesMap.module.css";
import { createInfoWindowHTML } from "./mapUtils";
import { venueLocations } from "./venueData";

declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

interface VenuesMapProps {
  className?: string;
}

const VenuesMap: React.FC<VenuesMapProps> = ({ className }) => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google) {
        initializeMap();
        return;
      }

      window.initMap = initializeMap;
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&callback=initMap`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    const initializeMap = () => {
      if (!mapRef.current) return;

      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 11,
        center: { lat: 30.337, lng: -97.69 },
        mapTypeId: 'roadmap',
      });

      let currentOpenOverlay: any = null;
      
      (window as any).currentOpenOverlay = null;

      venueLocations.forEach((location) => {
        // Create custom emoji icon
        const customIcon = {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="18" fill="white" stroke="#274a77" stroke-width="2"/>
              <text x="20" y="28" font-size="20" text-anchor="middle" font-family="Arial">${location.icon}</text>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20),
        };

        const marker = new window.google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: map,
          title: `${location.name} - ${location.type}`,
          icon: customIcon,
        });

        const createCustomOverlay = () => {
          if (currentOpenOverlay) {
            currentOpenOverlay.setMap(null);
            currentOpenOverlay = null;
          }
          
          if ((window as any).currentOpenOverlay) {
            (window as any).currentOpenOverlay.setMap(null);
            (window as any).currentOpenOverlay = null;
          }

          const overlayDiv = document.createElement('div');
          overlayDiv.innerHTML = createInfoWindowHTML(location);

          const newOverlay = new window.google.maps.OverlayView();
          newOverlay.onAdd = function() {
            this.getPanes().overlayMouseTarget.appendChild(overlayDiv);
          };
          
          newOverlay.onRemove = function() {
            if (overlayDiv.parentNode) {
              overlayDiv.parentNode.removeChild(overlayDiv);
            }
          };
          
          newOverlay.draw = function() {
            const projection = this.getProjection();
            const position = projection.fromLatLngToDivPixel(new window.google.maps.LatLng(location.lat, location.lng));
            overlayDiv.style.left = position.x + 'px';
            overlayDiv.style.top = position.y + 'px';
          };

          newOverlay.setMap(map);
          
          currentOpenOverlay = newOverlay;
          (window as any).currentOpenOverlay = newOverlay;
        };

        marker.addListener('click', () => {
          createCustomOverlay();
        });
      });
    };

    loadGoogleMaps();
  }, []);

  return (
    <div className={className}>
      <div className={styles.mapInstructions}>
        Click on any pin to view location details and get directions
      </div>
      <div ref={mapRef} className={styles.map}></div>
    </div>
  );
};

export default VenuesMap;
